import React from 'react'

import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import TextField from '@mui/material/TextField'

import { bannerSx, buttonSx, inputSx, type LifesHubBannerTone } from './lifeshub-tokens'
import {
    Field,
    FieldHelp,
    FieldLabel,
    FooterNote,
    ModalClose,
    ModalContent,
    ModalFooter,
    ModalFooterActions,
    ModalHead
} from './lifeshub-common'

export type LifesHubConfirmDialogTone = Exclude<LifesHubBannerTone, 'green'>

export type LifesHubConfirmDialogProps = {
    open: boolean
    title: string
    subtitle?: string
    tone: LifesHubConfirmDialogTone
    message: React.ReactNode
    confirmWord?: string
    typedWord?: string
    onTypedWordChange?: (value: string) => void
    confirmLabel?: string
    confirmIcon?: React.ReactNode
    cancelLabel?: string
    footerNote?: string
    onCancel: () => void
    onConfirm: () => void
}

export function LifesHubConfirmDialog({
    open,
    title,
    subtitle,
    tone,
    message,
    confirmWord,
    typedWord = '',
    onTypedWordChange,
    confirmLabel = 'Confirmar',
    confirmIcon,
    cancelLabel = 'Cancelar',
    footerNote,
    onCancel,
    onConfirm
}: LifesHubConfirmDialogProps) {
    const confirmed = !confirmWord || typedWord.trim().toUpperCase() === confirmWord

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            maxWidth={false}
            slotProps={{
                paper: {
                    sx: {
                        width: 'min(560px, calc(100vw - 28px))',
                        borderRadius: 'var(--radius)',
                        boxShadow: 'var(--shadow-2)',
                        backgroundImage: 'none'
                    }
                }
            }}
        >
            <ModalContent>
                <ModalHead>
                    <div>
                        <h2>{title}</h2>
                        {!!subtitle && <p>{subtitle}</p>}
                    </div>
                    <ModalClose aria-label="Fechar" onClick={onCancel}>
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </ModalClose>
                </ModalHead>

                <Alert
                    variant="outlined"
                    icon={
                        tone === 'blue' ? (
                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        ) : (
                            <WarningAmberIcon sx={{ fontSize: 16 }} />
                        )
                    }
                    sx={bannerSx(tone)}
                >
                    {message}
                </Alert>

                {!!confirmWord && (
                    <Field>
                        <FieldLabel htmlFor="lhConfirmWord">Confirmação</FieldLabel>
                        <TextField
                            id="lhConfirmWord"
                            size="small"
                            value={typedWord}
                            onChange={(event) => onTypedWordChange?.(event.target.value)}
                            placeholder={confirmWord}
                            autoComplete="off"
                            sx={inputSx}
                            fullWidth
                        />
                        <FieldHelp>
                            Digite <b>{confirmWord}</b> para liberar o botão de confirmação.
                        </FieldHelp>
                    </Field>
                )}
            </ModalContent>

            <ModalFooter>
                <FooterNote>{footerNote}</FooterNote>
                <ModalFooterActions>
                    <Button sx={buttonSx()} onClick={onCancel} autoFocus>
                        {cancelLabel}
                    </Button>
                    <Button
                        sx={buttonSx(tone === 'danger' ? 'danger' : 'primary')}
                        disabled={!confirmed}
                        onClick={onConfirm}
                        startIcon={confirmIcon}
                    >
                        {confirmLabel}
                    </Button>
                </ModalFooterActions>
            </ModalFooter>
        </Dialog>
    )
}

export default LifesHubConfirmDialog
